import React, { useEffect } from 'react';
import Navbar from '@/components/Navbar';
import HeroSection from '@/components/HeroSection';
import { updateMeta, setCanonicalUrl, setSocialTags } from '@/lib/canonical';

import TemplateGallery from '@/components/TemplateGallery';
import TestimonialsCarousel from '@/components/TestimonialsCarousel';
import worldrootLogo from '@/assets/worldroot-logo.png.asset.json';

import ContactSection from '@/components/ContactSection';
import Footer from '@/components/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import ConsentBanner from '@/components/ConsentBanner';

const Index = () => {
  // Handle smooth scrolling for anchor links with performance optimization
  useEffect(() => {
    const handleHashChange = () => {
      const { hash } = window.location;
      if (hash) {
        // Small delay to ensure DOM is ready
        requestAnimationFrame(() => {
          const element = document.getElementById(hash.slice(1));
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        });
      }
    };

    // Call once on mount in case there's already a hash in the URL
    handleHashChange();

    // Add listener for hash changes
    window.addEventListener('hashchange', handleHashChange, { passive: true });
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // SEO-specific meta tags
  useEffect(() => {
    // Update meta tags for SEO
    document.title = "Building Digital Experiences That Convert | AD Email Lab";

    updateMeta('description', 'I specialize in CRM, Marketing Automation, Web Development, Email Development, and Digital Marketing. Expertise across Salesforce, Adobe, Klaviyo, Google Ads, Meta Ads, and AI-powered tools to help businesses grow.');
    updateMeta('keywords', 'CRM, marketing automation, web development, email development, digital marketing, Salesforce, Klaviyo, Google Ads, Meta Ads');

    // Set canonical URL for homepage
    setCanonicalUrl('');
    setSocialTags({
      title: 'Building Digital Experiences That Convert',
      description: 'I specialize in CRM, Marketing Automation, Web Development, Email Development, and Digital Marketing. Expertise across Salesforce, Adobe, Klaviyo, Google Ads, Meta Ads, and AI-powered tools to help businesses grow.',
      path: '',
      type: 'website',
    });
  }, []);
  
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroSection />
      
      <TemplateGallery />
      <TestimonialsCarousel />

      <section className="py-20 px-4 relative overflow-hidden bg-[#0a1410] text-emerald-50">
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, rgba(45,212,191,0.18), transparent 55%), radial-gradient(circle at 80% 70%, rgba(16,185,129,0.15), transparent 60%), radial-gradient(circle at 50% 100%, rgba(120,83,47,0.18), transparent 60%)',
          }}
        />
        <div className="container mx-auto max-w-5xl relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-emerald-100 tracking-wide">
            Personal Projects
          </h2>
          <p className="text-emerald-100/80 text-lg mb-12 max-w-3xl">
            Beyond my professional work in CRM, marketing automation, and web development, I enjoy
            building personal projects that challenge my creativity and technical skills.
          </p>

          <div className="rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-[#0d1f18] via-[#0a1410] to-[#10241b] p-8 md:p-12 shadow-[0_0_60px_-20px_rgba(45,212,191,0.35)]">
            <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
              <div className="flex-shrink-0">
                <img
                  src={worldrootLogo.url}
                  alt="Worldroot: Awakening logo"
                  className="w-24 h-24 md:w-28 md:h-28 object-contain drop-shadow-[0_0_18px_rgba(45,212,191,0.45)]"
                  loading="lazy"
                />
              </div>

              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-bold text-emerald-100 tracking-wider">
                  Worldroot: Awakening
                </h3>
                <div className="text-emerald-300/80 text-sm uppercase tracking-[0.3em] mt-1 mb-6">
                  Browser-based Fantasy RPG
                </div>

                <p className="text-emerald-50/85 mb-4 leading-relaxed">
                  A browser-based fantasy RPG set in an ancient living forest. Designed and developed
                  as a personal passion project, it combines game design, interactive systems, user
                  experience, and programming to create an engaging adventure driven by exploration
                  and progression.
                </p>
                <p className="text-emerald-50/85 mb-8 leading-relaxed">
                  This project showcases my ability to build complete digital experiences from
                  concept to implementation, applying both creative thinking and technical problem
                  solving beyond the world of marketing and CRM.
                </p>

                <a
                  href="https://worldroot-awakening.lovable.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-emerald-400/60 bg-emerald-500/10 text-emerald-100 font-semibold tracking-wide uppercase text-sm hover:bg-emerald-400/20 hover:border-emerald-300 hover:shadow-[0_0_24px_-4px_rgba(45,212,191,0.6)] transition-all"
                >
                  Play Worldroot: Awakening
                  <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <ContactSection />
      <Footer />
      <ScrollToTop />
      <ConsentBanner />
    </div>
  );
};

export default Index;
